import type { ReactNode } from 'react';
import { useTranslation } from '../../core/hooks/use-translation';
import type { TranslationKey } from '../../core/i18n/translation';
import type { OfficeCity } from '../../core/models/office-city';
import type { Specialist } from '../../core/models/specialist';
import { StarRating } from '../../ui/star-rating';
import { CheckIcon, UsersIcon } from './booking-icons';

const CITY_LABEL: Record<OfficeCity, TranslationKey> = {
    Astana: 'site.city.astana',
    Almaty: 'site.city.almaty'
};

interface SpecialistStepProps {
    specialists: readonly Specialist[];
    loading: boolean;
    /** The picked consultant's identifier, or null for "whoever is free" — which is a real answer. */
    selected: string | null;
    errors: Record<string, TranslationKey>;
    onSelect: (memberId: string | null) => void;
    /** The wizard's error banner and button row, which the live page draws inside the card. */
    children: ReactNode;
}

/**
 * Step two: which consultant, or nobody in particular.
 *
 * **Skipping is a card, not a missing answer.** It is offered first and selected by default,
 * because most customers reach this form from the navigation rather than from a face on the home
 * page and have no opinion to express. Choosing it sends no identifier, the request arrives
 * unassigned, and unassigned is the staff queue — an administrator gives it to somebody, or the
 * member who takes it on assigns it to themselves. A required choice would make every one of those
 * customers pick a stranger.
 *
 * **The cards carry no portrait**, unlike the home page's. This is a list to choose from rather
 * than a gallery: photographs would make it scroll, and the customer who came here *for* a
 * particular face has already had that answered — they arrive with it picked.
 *
 * **It comes before the calendar**, and that ordering is the feature rather than a layout choice.
 * The times shown afterwards are that consultant's own free slots; asked after the calendar, the
 * question could only offer somebody who might not be free at the time already chosen.
 */
export function SpecialistStep({ specialists, loading, selected, errors, onSelect, children }: SpecialistStepProps) {
    const { t } = useTranslation();
    const error = errors['requestedMemberId'];

    return (
        <div className="card">
            <span className="card-lbl">{t('booking.s2s.step')}</span>
            <h1 className="card-title">{t('booking.s2s.title')}</h1>
            <p className="desc-txt whitespace-pre-line">{t('booking.s2s.hint')}</p>

            {loading ? (
                <p className="tp-loading">
                    <span className="spinner" aria-hidden="true" />
                    {t('booking.s2s.loading')}
                </p>
            ) : (
                <div className="spec-pick-cards" role="radiogroup" aria-label={t('booking.s2s.title')}>
                    <PickCard
                        chosen={selected === null}
                        name={t('booking.s2s.any')}
                        detail={t('booking.s2s.any_desc')}
                        mark={<UsersIcon />}
                        onSelect={() => onSelect(null)}
                    />

                    {specialists.map((specialist) => (
                        <PickCard
                            key={specialist.id}
                            chosen={specialist.id === selected}
                            name={specialist.displayName}
                            detail={describe(specialist, t(CITY_LABEL[specialist.city]))}
                            rating={
                                specialist.rating === null ? undefined : (
                                    <StarRating
                                        stars={specialist.rating}
                                        label={t('site.spec.rating').replace('{stars}', String(specialist.rating))}
                                        className="mt-0.5 text-xs"
                                    />
                                )
                            }
                            mark={initial(specialist.displayName)}
                            onSelect={() => onSelect(specialist.id)}
                        />
                    ))}
                </div>
            )}

            {/*
             * The list being empty is not an error and not a dead end: nobody has a public card
             * yet, and the request simply goes to the queue as it did before this step existed.
             */}
            {!loading && specialists.length === 0 && <p className="no-slots">{t('booking.s2s.none')}</p>}

            {error !== undefined && (
                <p className="fe" role="alert">
                    {t(error)}
                </p>
            )}

            {children}
        </div>
    );
}

/**
 * One choice. A `<button>` with `aria-checked`, the same construction the format and city cards
 * use — the pressed state has to survive being read aloud, not only being seen as a border colour.
 */
function PickCard({
    chosen,
    name,
    detail,
    mark,
    rating,
    onSelect
}: {
    chosen: boolean;
    name: string;
    detail: string;
    mark: ReactNode;
    rating?: ReactNode;
    onSelect: () => void;
}) {
    return (
        <button
            type="button"
            role="radio"
            aria-checked={chosen}
            className={chosen ? 'spec-pick-card sel-spec' : 'spec-pick-card'}
            onClick={onSelect}
        >
            <span className="spec-pick-mark" aria-hidden="true">
                {mark}
            </span>
            <span className="spec-pick-body">
                <span className="spec-pick-name">{name}</span>
                <span className="spec-pick-sub">{detail}</span>
                {rating !== undefined && <span className="block">{rating}</span>}
            </span>
            <span className="spec-pick-check" aria-hidden="true">
                <CheckIcon />
            </span>
        </button>
    );
}

/** The job title where there is one, and the office either way — the two facts a card can show. */
function describe(specialist: Specialist, city: string): string {
    return specialist.jobTitle === null ? city : `${specialist.jobTitle} · ${city}`;
}

/**
 * The first letter, in place of the portrait. Taken with `Array.from` rather than `charAt`, because
 * a name is not necessarily in the basic plane and half a surrogate pair renders as a box.
 */
function initial(displayName: string): string {
    return Array.from(displayName.trim())[0]?.toUpperCase() ?? '?';
}
